import {
  BaseBlok,
  BlokToComponentProps,
  CookieService,
  FormFieldValueProvider,
  GetComponentType,
  RocheXXFormProvider,
  slottedToComponents,
  useRecaptcha,
  useRocheXXFormContext,
  useSanitizedId,
  useTranslation,
} from '@repo/roche-xx-shared';
import { RocheUIButtonAppearance, RocheUIFormWrapper } from '@rocheglobal/roche-ui';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { ISbComponentType } from 'storyblok-js-client/dist/types/interfaces';
import { formCountryList } from '../../services/form/form-country-list';
import { formSubjectList } from '../../services/form/form-subject-list';
import { DropdownOption } from '../../services/form/types';

interface RocheBaseFormProps {
  children: ReactNode;
}

type RocheEmailFormProps = BlokToComponentProps<
  {
    hidden_email: string;
    hidden_title?: string;
    slotted: BaseBlok[];
    submit: string;
    submit_icon?: string;
    error: string;
    success: string;
    slotted_success_message?: Record<string, ISbComponentType<string> | ISbComponentType<string>[]>;
  } & BaseBlok
>;

type RocheContactFormProps = BlokToComponentProps<
  {
    slotted: BaseBlok[];
    submit: string;
    submit_icon?: string;
    error: string;
    error_message?: string;
    success: string;
    slotted_success_message?: Record<string, ISbComponentType<string> | ISbComponentType<string>[]>;
  } & BaseBlok
>;

type RocheGenericFormProps = BlokToComponentProps<
  {
    action?: string;
    slotted: BaseBlok[];
    submit: string;
    submit_icon?: string;
    error: string;
    error_message?: string;
    success: string;
    slotted_success_message?: Record<string, ISbComponentType<string> | ISbComponentType<string>[]>;
  } & BaseBlok
>;

const RocheForm = ({
  children,
  blok,
  getComponent,
}: RocheBaseFormProps & (RocheEmailFormProps | RocheContactFormProps | RocheGenericFormProps)) => {
  const form = useRocheXXFormContext();
  const [response, setResponse] = useState<string | null>(null);

  // Recaptcha & One-Trust props:
  const hasOptanonConsent = CookieService.useOptanonConsent();
  const generatedId = useSanitizedId();
  const recaptchaTargetDiv = `form-${blok._uid || generatedId}-recaptcha`;
  const { isLoaded, renderWidget, isError, resetWidget } = useRecaptcha(
    process.env.GATSBY_GOOGLE_RECAPTCHA_KEY,
    hasOptanonConsent,
  );
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const needsConsentText = useTranslation('confirm you are a human');
  const consentRequestButtonText = useTranslation('the test requires you to consent to targeting cookies');

  const formError = response === 'error';
  const formSuccess = response === 'success';

  // It would be nice not to display the success state inside the button, make things complex:
  const submitButtonText = formSuccess && !form.formDirty ? blok.success : blok.submit;
  const submitButtonIcon = formSuccess && !form.formDirty ? 'checkmark' : (blok.submit_icon ?? undefined);
  const submitButtonAppearance =
    formSuccess && !form.formDirty ? RocheUIButtonAppearance.SUCCESS : RocheUIButtonAppearance.PRIMARY;

  const message = formError ? blok.error : undefined;
  const messageType = formError ? 'error' : undefined;
  const successComponent = formSuccess ? slottedToComponents(getComponent, blok.slotted_success_message) : undefined;

  const handleSuccess = useCallback((newToken: string) => {
    setRecaptchaToken(newToken);
  }, []);

  const handleExpired = useCallback(() => {
    setRecaptchaToken(null);
  }, []);

  useEffect(() => {
    if (isLoaded && !isError) {
      renderWidget(recaptchaTargetDiv, handleSuccess, handleExpired);
    }
  }, [isLoaded, isError, renderWidget, handleSuccess, handleExpired]);

  useEffect(() => {
    if (response !== null && form.formDirty) {
      setResponse(null);
    }
  }, [form.formDirty]);

  return (
    <RocheUIFormWrapper
      action={form.action}
      needsConsent={!hasOptanonConsent}
      needsConsentText={needsConsentText}
      onConsentRequest={() => CookieService.showOnetrustSettings()}
      consentRequestButtonText={consentRequestButtonText}
      submitButton={{
        onSubmit: async (formEvent) => {
          const response = await form.handleSubmit(formEvent, recaptchaToken);
          setResponse(response);
          if (response === 'error' || response === 'success') {
            // Reset recaptcha on both success and error:
            resetWidget();
          }
          setRecaptchaToken(null);
        },
        disabled: formSuccess ? false : !form.allValuesOk || !recaptchaToken,
        text: submitButtonText,
        loading: form.isSubmitting,
        appearance: submitButtonAppearance,
        icon: submitButtonIcon,
      }}
      message={message}
      messageType={messageType}
      successComponent={successComponent}
    >
      {children}
      {isLoaded && !isError && <div className='form-wrapper--recaptcha-container' id={recaptchaTargetDiv}></div>}
    </RocheUIFormWrapper>
  );
};

const RocheFormEmail = ({ blok, getComponent }: RocheEmailFormProps): JSX.Element => {
  const formId = blok._uid;
  const formType = 'email';
  return (
    <RocheXXFormProvider formType={formType} formId={formId}>
      <RocheForm blok={blok} getComponent={getComponent}>
        {mapFormInputsToComponents(blok.slotted, getComponent)}
      </RocheForm>
    </RocheXXFormProvider>
  );
};

const RocheContactForm = ({ blok, getComponent }: RocheContactFormProps): JSX.Element => {
  const formId = blok._uid;
  const regionTranslation = useTranslation('region');
  const subjectTranslation = useTranslation('subject');
  const formType = 'contact';
  const customSlottedComponents: any = [
    {
      _uid: 'region',
      options: createOptions([...formCountryList]),
      label: regionTranslation,
      required: true,
      component: 'roche-dropdown',
    },
    {
      _uid: 'subject',
      options: createOptions([...formSubjectList]),
      label: subjectTranslation,
      required: true,
      component: 'roche-dropdown',
    },
    // See formContext function satisfiesConditionalDisplay()
    ...blok.slotted.flatMap((slot) => {
      if (slot.label === OCCUPATION_DROPDOWN_FORM_LABEL) {
        return [
          slot,
          {
            ...conditionalOtherOccupationField,
            conditionalDisplay: {
              dependsOn: slot._uid,
              value: OCCUPATION_SHOULD_MATCH_ON_VALUE,
            },
          },
        ];
      }
      return [slot];
    }),
  ];

  return (
    <RocheXXFormProvider formType={formType} formId={formId}>
      <RocheForm blok={blok} getComponent={getComponent}>
        {mapFormInputsToComponents(customSlottedComponents, getComponent)}
      </RocheForm>
    </RocheXXFormProvider>
  );
};

const RocheGenericForm = ({ blok, getComponent }: RocheGenericFormProps) => {
  const formId = blok._uid;
  const formType = 'multi-step'; // Deprecated?
  const action = blok.action;
  return (
    <RocheXXFormProvider formType={formType} formId={formId} action={action}>
      <RocheForm blok={blok} getComponent={getComponent}>
        {mapFormInputsToComponents(blok.slotted, getComponent)}
      </RocheForm>
    </RocheXXFormProvider>
  );
};

export const forms = {
  'roche-form-contact': RocheContactForm,
  'roche-form-email': RocheFormEmail,
  // Does not seem in use and does not have a type resolver on Roche-XX-Forms-API
  'roche-form': RocheGenericForm,
  // Deprecated - remove from storyblok first:
  'roche-form-newsletter': () => null,
};

function createOptions(values: string[]): DropdownOption[] {
  return values.map((value) => ({ label: value, value }));
}

const isStatefulFormInputComponent = (componentType: string) => componentType !== 'roche-richtext';

const mapFormInputsToComponents = (slotted: any, getComponent: GetComponentType): ReactElement[] => {
  return slotted.map((slot) => {
    // @ts-expect-error we can't type the slots still
    const formComponent = slottedToComponents(getComponent, [slot]);
    const inputWithContext = (
      <FormFieldValueProvider slot={slot} key={slot._uid}>
        {formComponent}
      </FormFieldValueProvider>
    );

    if (isStatefulFormInputComponent(slot.component)) {
      return slot.conditionalDisplay ? (
        <ConditionalRenderer id={slot._uid} key={slot._uid}>
          {inputWithContext}
        </ConditionalRenderer>
      ) : (
        inputWithContext
      );
    } else {
      return formComponent;
    }
  });
};

// This is currently the only use-case for conditional rendering of input fields in a form
// This should all move to storyblok when the use-case gets bigger. We manually insert a roche-input field here
// when the "Occupation".value === "Other". See formContext function satisfiesConditionalDisplay()
const OCCUPATION_DROPDOWN_FORM_LABEL = 'Occupation';
const OCCUPATION_SHOULD_MATCH_ON_VALUE = 'Other';
const conditionalOtherOccupationField = {
  _uid: 'otherOccupation',
  label: 'Other',
  required: true,
  type: 'text',
  name: 'otherOccupation',
  component: 'roche-input',
};

function ConditionalRenderer({ children, id }) {
  const form = useRocheXXFormContext();
  const shouldRender = form.satisfiesConditionalDisplay(id);
  return shouldRender ? <>{children}</> : null;
}
